import { useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { Briefcase } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { SessionSwitcher } from "@/components/ui/session-switcher";
import { NAVIGATION_DATA } from "@/config/navigation";
import type { SessionStatus } from "@/types/common";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useGetRiskAnalysisBySession } from "@/hooks/api/query/useGetRiskAnalysisBySession";
import { useIsMobile } from "@/hooks/use-mobile";
import { AppSidebar } from "./app-sidebar";
import { MobileSidebar, MobileSidebarTrigger } from "./mobile-sidebar";
import { UserAvatarDropdown } from "./user-avatar-dropdown";

type MainLayoutProps = {
  children: React.ReactNode;
};

export function MainLayout({ children }: MainLayoutProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const isWizardRoute = location.pathname === "/identificare-raportare";

  const { user } = useAuth();
  const { currentSessionId, currentSession } = useSessionSwitcher();
  const { data: entityDataResponse } = useGetEntityData(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: 1,
  });

  const { data: riskData } = useGetRiskAnalysisBySession(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: false,
  });

  const entityData = entityDataResponse?.entity_data;

  return (
    <SidebarProvider defaultOpen={!isWizardRoute}>
      <div className="min-h-screen flex w-full">
        <AppSidebar />

        <div className="flex-1 flex flex-col">
          <header className="h-12 flex items-center justify-between px-4 border-b border-ds-border-800 bg-navy-900 relative overflow-hidden">
            {/* Mobile Menu Button */}
            {isMobile && (
              <MobileSidebarTrigger
                onClick={() => setIsMobileSidebarOpen(true)}
              />
            )}

            <div className="flex-1" />
            <UserAvatarDropdown />
          </header>

          {/* Mobile Sidebar */}
          {isMobile && (
            <MobileSidebar
              isOpen={isMobileSidebarOpen}
              onOpenChange={setIsMobileSidebarOpen}
              navigationData={NAVIGATION_DATA}
              entityData={entityData}
              riskData={riskData}
              sessionId={currentSessionId}
              sessionStatus={(currentSession?.status as SessionStatus) ?? null}
              isAuthenticated={!!user}
              userEmail={user?.email}
              sessionSwitcherSlot={
                user ? (
                  <>
                    <SessionSwitcher entityData={entityData} />
                    <NavLink
                      to="/companiile-mele"
                      data-testid="sidebar-companiile-mele-mobile"
                      onClick={() => setIsMobileSidebarOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "mt-3 flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all",
                          isActive
                            ? "bg-ds-blue-50 text-ds-blue-700"
                            : "text-ds-text-600 hover:bg-ds-surface-50"
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Briefcase
                            size={18}
                            className={
                              isActive ? "text-ds-blue-600" : "text-ds-text-400"
                            }
                          />
                          <span className="font-medium font-heading text-sm">
                            Companiile mele
                          </span>
                        </>
                      )}
                    </NavLink>
                  </>
                ) : null
              }
            />
          )}

          <main className="flex-1 bg-background">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
}
